import { useMediaUrl } from "../echoflicks/useMediaUrl";
import type { MediaRef } from "./types";

type ExperimentImageProps = {
  media: MediaRef | undefined;
  alt: string;
  className?: string;
  onClick?: () => void;
  /** Called with the signed URL once resolved (used by the wipe view, which composites two images). */
  onUrl?: (url: string) => void;
};

/**
 * One experiment image, resolved to a signed URL through the same media path the rest of the CRM uses
 * (dashboard API `/media/sign`, 1 h TTL). Renders a neutral placeholder while signing and a short
 * error line if the object cannot be signed (e.g. the bucket is not in the API's allow-list).
 */
export const ExperimentImage = ({
  media,
  alt,
  className,
  onClick,
}: ExperimentImageProps) => {
  const state = useMediaUrl(
    media ? { storage_bucket: media.bucket, storage_path: media.key } : null,
  );
  if (!media) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-xs text-muted-foreground ${className ?? ""}`}
      >
        —
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-xs text-destructive p-2 ${className ?? ""}`}
      >
        {state.error}
      </div>
    );
  }
  if (!state.url) {
    return <div className={`animate-pulse bg-muted ${className ?? ""}`} />;
  }
  return (
    <img
      src={state.url}
      alt={alt}
      width={media.width}
      height={media.height}
      loading="lazy"
      decoding="async"
      className={className}
      onClick={onClick}
      draggable={false}
    />
  );
};

/** Resolve a signed URL without rendering (for the wipe compare, which needs two URLs in one element). */
export function useExperimentImageUrl(media: MediaRef | undefined) {
  const state = useMediaUrl(
    media ? { storage_bucket: media.bucket, storage_path: media.key } : null,
  );
  return state.url;
}
